/**
 * React hook for sandbox persistence
 * 
 * Manages the state of saving/loading sandboxes and integrates with URL routing.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import type {
  GboxDocument,
  SaveSandboxResponse,
  PersistedSandbox,
} from '@gotti/sandbox-shared';
import { readGottiRunbooks, RevisionConflictError, EditTokenError } from '@gotti/sandbox-shared';
import { seedRunbooks } from '../agent/runbookStore';
import {
  getCurrentSandboxFromUrl,
  loadSandbox,
  saveSandbox,
  saveNewRevision,
  forkWithContent,
  checkBackendHealth,
  copyShareUrl,
  getShareUrl,
} from './client';

export interface PersistenceState {
  /** Whether the backend is available */
  isBackendAvailable: boolean;
  /** Whether we're currently loading a sandbox */
  isLoading: boolean;
  /** Whether we're currently saving */
  isSaving: boolean;
  /** The currently loaded sandbox (if loaded from backend) */
  currentSandbox: PersistedSandbox | null;
  /** Available revisions for the current sandbox */
  revisions: { revision: number; createdAt: string; name: string }[];
  /** The latest revision number */
  latestRevision: number | null;
  /** Whether the project changed since it was last saved or loaded (`markDirty`) */
  hasUnsavedChanges: boolean;
  /** The revision the last save of this session wrote, until the next load: what "Saved — revision N" names */
  savedRevision: number | null;
  /** Error message if any operation failed */
  error: string | null;
}

export interface PersistenceActions {
  /** Save the current state as a new sandbox */
  save: (data: GboxDocument) => Promise<SaveSandboxResponse | null>;
  /** Save as a new revision of the current sandbox */
  saveRevision: (data: GboxDocument) => Promise<SaveSandboxResponse | null>;
  /** Fork with current content - always creates new hash */
  fork: (data: GboxDocument) => Promise<SaveSandboxResponse | null>;
  /** Load a sandbox by hash/revision */
  load: (hash: string, revision?: number) => Promise<GboxDocument | null>;
  /** Load from the current URL */
  loadFromUrl: () => Promise<GboxDocument | null>;
  /** Copy share URL to clipboard */
  copyShareUrl: () => Promise<boolean>;
  /** Get the share URL */
  getShareUrl: () => string | null;
  /** Mark that the project changed — an operation, an edit in the editor, an asset: it has unsaved changes */
  markDirty: () => void;
  /** Mark that the editor opened a project (a template, an import, a reset): nothing of it is unsaved, no save to name */
  markClean: () => void;
  /** Clear error */
  clearError: () => void;
  /** Forget the loaded sandbox — the editor did not open it — so the next Save makes a new sandbox, not a revision of it */
  detach: () => void;
}

export function useSandboxPersistence(): [PersistenceState, PersistenceActions] {
  const [state, setState] = useState<PersistenceState>({
    isBackendAvailable: false,
    isLoading: false,
    isSaving: false,
    currentSandbox: null,
    revisions: [],
    latestRevision: null,
    hasUnsavedChanges: false,
    savedRevision: null,
    error: null,
  });
  
  // Track if we've already loaded from URL
  const hasLoadedFromUrl = useRef(false);

  // Every change is counted. A save sends the project as it was when it started, so it leaves the project clean only
  // when the count has not moved since: a change made while the save was in flight is still unsaved after it.
  const changes = useRef(0);
  /** The state after a save that sent the project as it was at change `at`, and was stored as `revision`. */
  const savedAt = (at: number, revision: number): Pick<PersistenceState, 'hasUnsavedChanges' | 'savedRevision' | 'error'> =>
    ({ hasUnsavedChanges: changes.current !== at, savedRevision: revision, error: null });
  /** The state after a load: nothing changed since, and no save of this session to name. */
  const loaded = (): Pick<PersistenceState, 'hasUnsavedChanges' | 'savedRevision'> => ({ hasUnsavedChanges: false, savedRevision: null });
  
  // Check backend availability on mount
  useEffect(() => {
    checkBackendHealth()
      .then(available => {
        setState(s => ({ ...s, isBackendAvailable: available }));
      })
      .catch(() => {
        // Silently fail - persistence is optional
        setState(s => ({ ...s, isBackendAvailable: false }));
      });
  }, []);
  
  // Listen for navigation events
  useEffect(() => {
    const handleNavigate = (e: Event) => {
      const customEvent = e as CustomEvent<{ hash: string; revision?: number }>;
      const { hash, revision } = customEvent.detail;
      actions.load(hash, revision);
    };
    
    const handlePopState = () => {
      const parsed = getCurrentSandboxFromUrl();
      if (parsed) {
        actions.load(parsed.hash, parsed.revision);
      }
    };
    
    window.addEventListener('sandbox-navigate', handleNavigate);
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('sandbox-navigate', handleNavigate);
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);
  
  const actions: PersistenceActions = {
    save: useCallback(async (data: GboxDocument) => {
      if (!state.isBackendAvailable) {
        setState(s => ({ ...s, error: 'Backend not available' }));
        return null;
      }
      
      setState(s => ({ ...s, isSaving: true, error: null }));
      const at = changes.current;
      
      try {
        const response = await saveSandbox(data);

        // Load the saved sandbox to update state
        const stored = await loadSandbox(response.hash, response.revision);

        setState(s => ({
          ...s,
          isSaving: false,
          currentSandbox: stored.sandbox,
          revisions: stored.revisions,
          latestRevision: stored.latestRevision,
          ...savedAt(at, response.revision),
        }));

        return response;
      } catch (error) {
        setState(s => ({
          ...s,
          isSaving: false,
          error: error instanceof Error ? error.message : 'Failed to save',
        }));
        return null;
      }
    }, [state.isBackendAvailable]),

    saveRevision: useCallback(async (data: GboxDocument) => {
      if (!state.isBackendAvailable || !state.currentSandbox) {
        setState(s => ({ ...s, error: 'No sandbox to save revision for' }));
        return null;
      }
      setState(s => ({ ...s, isSaving: true, error: null }));
      const at = changes.current;
      try {
        const response = await saveNewRevision(data, state.currentSandbox.hash, state.currentSandbox.revision);
        const stored = await loadSandbox(response.hash, response.revision);
        setState(s => ({
          ...s, isSaving: false, currentSandbox: stored.sandbox, revisions: stored.revisions,
          latestRevision: stored.latestRevision, ...savedAt(at, response.revision),
        }));
        return response;
      } catch (error) {
        if (error instanceof RevisionConflictError) {
          const reload = window.confirm(
            `This sandbox was updated elsewhere (head is now revision ${error.headRevision}).\n` +
            `OK = reload the latest revision (discards your unsaved edits).\nCancel = fork into a new sandbox.`,
          );
          if (reload) {
            const latest = await loadSandbox(state.currentSandbox!.hash);
            // The editor still holds the edits this save sent (this state names the latest revision, but nothing puts
            // it in the editor), so they stay unsaved — saying "clean" here would hide them.
            setState(s => ({
              ...s, isSaving: false, currentSandbox: latest.sandbox, revisions: latest.revisions,
              latestRevision: latest.latestRevision, savedRevision: null, error: null,
            }));
            return null;
          }
          const forkResp = await forkWithContent(data, state.currentSandbox!.hash);
          const stored = await loadSandbox(forkResp.hash, forkResp.revision);
          setState(s => ({
            ...s, isSaving: false, currentSandbox: stored.sandbox, revisions: stored.revisions,
            latestRevision: stored.latestRevision, ...savedAt(at, forkResp.revision),
          }));
          return forkResp;
        }
        if (error instanceof EditTokenError) {
          const doFork = window.confirm(
            `You don't have an edit token for this sandbox (it was created elsewhere or the token was lost).\nFork to keep your changes?`,
          );
          if (doFork) {
            const forkResp = await forkWithContent(data, state.currentSandbox!.hash);
            const stored = await loadSandbox(forkResp.hash, forkResp.revision);
            setState(s => ({
              ...s, isSaving: false, currentSandbox: stored.sandbox, revisions: stored.revisions,
              latestRevision: stored.latestRevision, ...savedAt(at, forkResp.revision),
            }));
            return forkResp;
          }
          setState(s => ({ ...s, isSaving: false, error: 'Edit token required — fork to keep editing' }));
          return null;
        }
        setState(s => ({ ...s, isSaving: false, error: error instanceof Error ? error.message : 'Failed to save revision' }));
        return null;
      }
    }, [state.isBackendAvailable, state.currentSandbox]),

    fork: useCallback(async (data: GboxDocument) => {
      if (!state.isBackendAvailable) {
        setState(s => ({ ...s, error: 'Backend not available' }));
        return null;
      }
      
      setState(s => ({ ...s, isSaving: true, error: null }));
      const at = changes.current;
      
      try {
        // Fork with current content - creates new hash
        const parentHash = state.currentSandbox?.hash;
        const response = await forkWithContent(data, parentHash);

        // Load the forked sandbox
        const stored = await loadSandbox(response.hash, response.revision);

        setState(s => ({
          ...s,
          isSaving: false,
          currentSandbox: stored.sandbox,
          revisions: stored.revisions,
          latestRevision: stored.latestRevision,
          ...savedAt(at, response.revision),
        }));

        return response;
      } catch (error) {
        setState(s => ({
          ...s,
          isSaving: false,
          error: error instanceof Error ? error.message : 'Failed to fork',
        }));
        return null;
      }
    }, [state.isBackendAvailable, state.currentSandbox]),

    load: useCallback(async (hash: string, revision?: number) => {
      // Skip if backend not available - don't show error for optional feature
      if (!state.isBackendAvailable) {
        return null;
      }
      
      setState(s => ({ ...s, isLoading: true, error: null }));
      
      try {
        const response = await loadSandbox(hash, revision);
        seedRunbooks(readGottiRunbooks(response.sandbox.data));

        setState(s => ({
          ...s,
          isLoading: false,
          currentSandbox: response.sandbox,
          revisions: response.revisions,
          latestRevision: response.latestRevision,
          ...loaded(),
        }));

        return response.sandbox.data;
      } catch (error) {
        setState(s => ({
          ...s,
          isLoading: false,
          error: error instanceof Error ? error.message : 'Failed to load',
        }));
        return null;
      }
    }, [state.isBackendAvailable]),

    loadFromUrl: useCallback(async () => {
      // Only load once
      if (hasLoadedFromUrl.current) {
        return state.currentSandbox?.data ?? null;
      }
      
      const parsed = getCurrentSandboxFromUrl();
      if (!parsed) {
        hasLoadedFromUrl.current = true;
        return null;
      }
      
      // Skip if backend not available
      if (!state.isBackendAvailable) {
        return null;
      }
      
      hasLoadedFromUrl.current = true;
      
      // Load directly instead of going through actions.load to avoid stale closure
      setState(s => ({ ...s, isLoading: true, error: null }));
      
      try {
        const response = await loadSandbox(parsed.hash, parsed.revision);
        seedRunbooks(readGottiRunbooks(response.sandbox.data));

        setState(s => ({
          ...s,
          isLoading: false,
          currentSandbox: response.sandbox,
          revisions: response.revisions,
          latestRevision: response.latestRevision,
          ...loaded(),
        }));

        return response.sandbox.data;
      } catch (error) {
        setState(s => ({
          ...s,
          isLoading: false,
          error: error instanceof Error ? error.message : 'Failed to load',
        }));
        return null;
      }
    }, [state.currentSandbox, state.isBackendAvailable]),
    
    copyShareUrl: useCallback(async () => {
      if (!state.currentSandbox) return false;
      return copyShareUrl(state.currentSandbox.hash, state.currentSandbox.revision);
    }, [state.currentSandbox]),
    
    getShareUrl: useCallback(() => {
      if (!state.currentSandbox) return null;
      return getShareUrl(state.currentSandbox.hash, state.currentSandbox.revision);
    }, [state.currentSandbox]),
    
    markDirty: useCallback(() => {
      changes.current += 1;
      setState(s => (s.hasUnsavedChanges ? s : { ...s, hasUnsavedChanges: true }));
    }, []),

    markClean: useCallback(() => {
      setState(s => ({ ...s, ...loaded(), error: null }));
    }, []),
    
    clearError: useCallback(() => {
      setState(s => ({ ...s, error: null }));
    }, []),

    detach: useCallback(() => {
      setState(s => ({ ...s, currentSandbox: null, revisions: [], latestRevision: null, ...loaded() }));
    }, []),
  };
  
  return [state, actions];
}
